import React, { useState } from 'react';
import {
    Plus,
    Upload,
    Download,
    Edit2,
    Trash2,
    FileText,
    Eye
} from 'lucide-react';
import { Button } from './Button';

export const PurchaseDetails = () => {
    const [activeTab, setActiveTab] = useState('purchases');

    const [purchaseInvoices] = useState([
        {
            id: '1',
            billNo: 'PUR001',
            date: '2024-01-15',
            vendor: 'Office Supplies Ltd.',
            amount: 12000,
            gst: 2160,
            rcm: false,
            status: 'Verified',
        },
        {
            id: '2',
            billNo: 'PUR002',
            date: '2024-01-16',
            vendor: 'Tech Solutions Inc.',
            amount: 8000,
            gst: 1440,
            rcm: true,
            status: 'Pending',
        },
    ]);

    const tabs = [
        { id: 'purchases', label: 'Purchase Bills', count: 15 },
        { id: 'expenses', label: 'Expense Invoices', count: 8 },
        { id: 'imports', label: 'Imports', count: 3 },
        { id: 'rcm', label: 'RCM Bills', count: 5 },
    ];

    const totalPurchases = purchaseInvoices.reduce((sum, invoice) => sum + invoice.amount, 0);
    const totalGST = purchaseInvoices.reduce((sum, invoice) => sum + invoice.gst, 0);
    const totalRCM = purchaseInvoices
        .filter((invoice) => invoice.rcm)
        .reduce((sum, invoice) => sum + invoice.gst, 0);

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Purchase Details (Inward Supply)</h2>
                <div className="flex space-x-2">


                    <Button label="Import Excel" />
                    <Button label="Add Bill" />

                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            {tab.label}
                            <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2 rounded-full text-xs">
                                {tab.count}
                            </span>
                        </button>
                    ))}
                </nav>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-orange-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-orange-900">Total Purchases</h3>
                    <p className="text-2xl font-bold text-orange-600">₹{totalPurchases.toLocaleString()}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-blue-900">Input Tax Credit</h3>
                    <p className="text-2xl font-bold text-blue-600">₹{totalGST.toLocaleString()}</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-red-900">RCM Liability</h3>
                    <p className="text-2xl font-bold text-red-600">₹{totalRCM.toLocaleString()}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-green-900">Net ITC</h3>
                    <p className="text-2xl font-bold text-green-600">₹{(totalGST - totalRCM).toLocaleString()}</p>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Bill Details
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Vendor
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Amount
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                GST
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                RCM
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {purchaseInvoices.map((invoice) => (
                            <tr key={invoice.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div>
                                        <div className="text-sm font-medium text-gray-900">{invoice.billNo}</div>
                                        <div className="text-sm text-gray-500">{invoice.date}</div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {invoice.vendor}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    ₹{invoice.amount.toLocaleString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    ₹{invoice.gst.toLocaleString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span
                                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${invoice.rcm ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                                            }`}
                                    >
                                        {invoice.rcm ? 'Yes' : 'No'}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span
                                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${invoice.status === 'Verified'
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-yellow-100 text-yellow-800'
                                            }`}
                                    >
                                        {invoice.status}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <div className="flex space-x-2">
                                        <button className="text-blue-600 hover:text-blue-900">
                                            <Eye className="h-4 w-4" />
                                        </button>
                                        <button className="text-gray-600 hover:text-gray-900">
                                            <Edit2 className="h-4 w-4" />
                                        </button>
                                        <button className="text-red-600 hover:text-red-900">
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
